import { Badge, BadgeColorVariant, BadgeProps, BadgeVariant } from "@/components/ui/badge";
import { EmptyValue } from "@/components/ui/empty-value";
import { ChevronUp } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface ExpandableBadgeItem {
  key: string;
  label: string;
  variant?: BadgeProps["variant"];
  colorVariant?: BadgeProps["colorVariant"];
}

interface ExpandableBadgeListProps {
  items: ExpandableBadgeItem[];
  defaultVisible?: number;
}

export function ExpandableBadgeList({ items, defaultVisible = 2 }: ExpandableBadgeListProps) {
  const { t } = useTranslation("common");
  const [isExpanded, setIsExpanded] = useState(false);

  if (items.length === 0) {
    return (
      <div className="text-left">
        <EmptyValue />
      </div>
    );
  }

  const hiddenCount = items.length - defaultVisible;
  const hasHidden = hiddenCount > 0;
  const displayedItems = isExpanded ? items : items.slice(0, defaultVisible);

  const handleExpand = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsExpanded(true);
  };

  const handleCollapse = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsExpanded(false);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap gap-1 pr-1">
        {displayedItems.map((item) => (
          <Badge
            variant={item.variant ?? BadgeVariant.Outlined}
            colorVariant={item.colorVariant ?? BadgeColorVariant.Secondary}
            key={item.key}
          >
            {item.label}
          </Badge>
        ))}
      </div>
      {!isExpanded && hasHidden && (
        <Badge
          variant={BadgeVariant.Filled}
          colorVariant={BadgeColorVariant.Secondary}
          className="w-fit cursor-pointer"
          onClick={handleExpand}
        >
          +{hiddenCount} {t("More")}
        </Badge>
      )}
      {isExpanded && hasHidden && (
        <Badge
          variant={BadgeVariant.Filled}
          colorVariant={BadgeColorVariant.Secondary}
          className="w-fit cursor-pointer"
          onClick={handleCollapse}
        >
          <ChevronUp className="mr-1 h-3 w-3" />
          {t("Collapse")}
        </Badge>
      )}
    </div>
  );
}
